// La fenêtre d'écriture d'un commentaire — affichée DANS la barre « Mes commentaires » (ou, hors
// de l'observatoire, dans le panneau « Comprendre ce chiffre ») : même arbre `.obs2`, pas de
// second portail qui tomberait blanc sur blanc hors des styles de l'observatoire (#508).
//
// #563 (Marc, 08/10) : « qu'il puisse commenter pour lui, son équipe, TerObs ». Le niveau se
// choisit AVANT le champ, en clair, chacun avec ce qu'il veut dire (CDC I, § 5.1) :
//   · « Pour moi »   — vous seul ;
//   · « Mon équipe » — les membres de l'espace ; proposé aux seuls membres officiels ;
//   · « À TerObs »   — l'équipe TerObs, qui répond ; vos collègues ne le voient pas.
// Le niveau proposé d'office suit `niveauParDefaut` (« Mon équipe » pour un membre, sinon « À
// TerObs ») ; un geste explicite (« Écrire à TerObs ») impose le sien. Un bandeau répète le
// niveau au-dessus du champ et le bouton d'envoi le nomme : on ne publie jamais par mégarde.
// La nature (erreur, question, suggestion) ne vaut que pour TerObs : elle trie sa boîte.
// Texte brut ; rappel sur les données personnelles. Le navigateur n'envoie que l'ancre
// (identifiants) : titres et contexte sont reconstruits par le serveur.
import { useEffect, useId, useRef, useState } from 'react'
import { Lock, Send, Users } from 'lucide-react'
import { NOM_PRODUIT } from '../../constants'
import {
  ecrireCommentaire, NATURES, NIVEAUX, niveauParDefaut, TAILLE_MAX,
  type Message, type Nature, type Niveau,
} from './api'
import type { CanalTerObs } from './contexte'

const ICONES: Record<Niveau, typeof Lock> = { moi: Lock, equipe: Users, terobs: Send }

/** Le badge d'un niveau : l'icône ET le mot, jamais la seule couleur (CDC I, § 4). */
export function BadgeNiveau({ niveau }: { niveau: Niveau }) {
  const Icone = ICONES[niveau]
  return (
    <span className={`obs2-niveau-badge niveau-${niveau}`}>
      <Icone size={11} aria-hidden="true" /> {NIVEAUX[niveau].badge}
    </span>
  )
}

export function FenetreCommentaire({ canal, graphique, tuile, titre, onFermer, onEnvoye,
                                     dansLaBarre = false, niveauInitial }: {
  canal: CanalTerObs; graphique?: string; tuile?: string; titre: string; onFermer: () => void
  onEnvoye?: (m: Message) => void; dansLaBarre?: boolean; niveauInitial?: Niveau
}) {
  const proposes: Niveau[] = canal.membre ? ['moi', 'equipe', 'terobs'] : ['moi', 'terobs']
  const [niveau, setNiveau] = useState<Niveau>(() => {
    const n = niveauInitial ?? niveauParDefaut(canal.membre)
    return proposes.includes(n) ? n : 'terobs'
  })
  const [texte, setTexte] = useState('')
  const [nature, setNature] = useState<Nature | null>(null)
  const [etat, setEtat] = useState<'saisie' | 'envoi' | 'envoye'>('saisie')
  const [erreur, setErreur] = useState<string | null>(null)
  const champ = useId()
  const aide = useId()
  const groupe = useId()
  const zone = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { if (dansLaBarre) zone.current?.focus() }, [dansLaBarre])

  const envoyer = async () => {
    if (!texte.trim() || etat === 'envoi') return
    setEtat('envoi'); setErreur(null)
    try {
      const m = await ecrireCommentaire(canal.insee, {
        ...(canal.page ? { page: canal.page } : {}),
        ...(canal.section ? { section: canal.section } : {}),
        ...(graphique ? { graphique } : tuile ? { tuile } : {}),
      }, texte, nature, niveau, canal.token)
      if (onEnvoye) { onEnvoye(m); return }
      setEtat('envoye')
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Le message n’a pas pu être envoyé.')
      setEtat('saisie')
    }
  }

  if (etat === 'envoye') {
    return (
      <div className="obs2-terobs" role="status">
        <p className="obs2-terobs-ok">
          {niveau === 'terobs' ? `Message envoyé à l’équipe ${NOM_PRODUIT}.` : NIVEAUX[niveau].envoye}
        </p>
        <p className="obs2-terobs-aide">
          Retrouvez-le, avec les réponses, dans « Mes commentaires » (en haut de la page).
        </p>
        <button type="button" className="obs2-btn obs2-btn-ghost" onClick={onFermer}>Fermer</button>
      </div>
    )
  }

  const Icone = ICONES[niveau]
  return (
    <form className="obs2-terobs" onSubmit={e => { e.preventDefault(); void envoyer() }}>
      <fieldset className="obs2-niveaux">
        <legend>Qui pourra le lire ?</legend>
        {proposes.map(n => {
          const I = ICONES[n]
          return (
            <label key={n} className={`obs2-niveau-choix niveau-${n}${niveau === n ? ' choisi' : ''}`}>
              <input type="radio" name={`niveau-${groupe}`} value={n} checked={niveau === n}
                onChange={() => setNiveau(n)} />
              <span className="obs2-niveau-titre"><I size={13} aria-hidden="true" /> {NIVEAUX[n].titre}</span>
              <span className="obs2-niveau-qui">{NIVEAUX[n].qui}</span>
            </label>
          )
        })}
      </fieldset>
      <div className={`obs2-terobs-niveau niveau-${niveau}`}>
        <Icone size={13} aria-hidden="true" /> <strong>{NIVEAUX[niveau].titre}</strong> — {NIVEAUX[niveau].qui}
      </div>
      {/* Dans la barre, l'ancre lisible est déjà affichée au-dessus de la fenêtre. */}
      {!dansLaBarre && <p className="obs2-terobs-sujet">À propos de : <em>{titre}</em></p>}
      {niveau === 'terobs' && (
        <fieldset className="obs2-terobs-natures">
          <legend>Nature (facultatif)</legend>
          {NATURES.map(n => (
            <label key={n.v}>
              <input type="radio" name={`nature-terobs-${groupe}`} value={n.v} checked={nature === n.v}
                onChange={() => setNature(n.v)} /> {n.label}
            </label>
          ))}
        </fieldset>
      )}
      <label htmlFor={champ} className="obs2-terobs-label">Votre message</label>
      <textarea id={champ} ref={zone} aria-describedby={aide} className="obs2-terobs-texte"
        rows={dansLaBarre ? 5 : 6}
        maxLength={TAILLE_MAX} value={texte} onChange={e => setTexte(e.target.value)} required />
      <p id={aide} className="obs2-terobs-aide">
        Texte brut, {TAILLE_MAX.toLocaleString('fr-FR')} caractères au plus
        ({texte.length.toLocaleString('fr-FR')} saisis). Évitez d’écrire des informations
        personnelles sur des habitants ou des agents.
      </p>
      {erreur && <p className="obs2-terobs-erreur" role="alert">{erreur}</p>}
      <div className="obs2-terobs-actions">
        <button type="button" className="obs2-btn obs2-btn-ghost" onClick={onFermer}>Annuler</button>
        <button type="submit" className="obs2-btn obs2-btn-primary"
          disabled={!texte.trim() || etat === 'envoi'}>
          {etat === 'envoi' ? 'Envoi…' : NIVEAUX[niveau].envoyer}
        </button>
      </div>
    </form>
  )
}
